import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Dialog, Grid, Tab } from '@mui/material';
import { TabContext } from '@mui/lab';
import { useGetFilteredAgreementsQuery } from 'store/agreements/agreements';
import StyledTabList from 'config/material-ui/styled/TabList.styled';
import usePagination from 'hooks/usePagination';
import AgreementsList from 'components/AgreementsList';
import { useTranslation } from 'react-i18next';
import { NavBarContainer, NavBarTitle } from 'common/NavBar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddAgreementForm from 'components/AddAgreementForm';
import CustomPagination from 'common/CustomPagination/CustomPagination';
import emptyApiResponse from 'store/emptyApiResponse';
import { Agreement } from 'store/agreements/interfaces';
import Filters from 'components/AgreementsList/Filters/Filters';

export enum AgreementsTabValue {
  active = 'active',
  inactive = 'inactive',
}

function Agreements() {
  const [searchParams, setSearchParams] = useSearchParams({});
  const [tabValue, setTabValue] = useState(() => {
    const isActive = searchParams.get('isActive');
    if (!isActive) {
      return AgreementsTabValue.active;
    }

    return JSON.parse(isActive)
      ? AgreementsTabValue.active
      : AgreementsTabValue.inactive;
  });
  const [isOpen, setIsOpen] = useState(false);

  const { page, handleOnPageChange, handleOnItemsPerPageChange, itemsPerPage } =
    usePagination({
      searchParams,
      setSearchParams,
    });

  const {
    data: agreementsData = {
      'hydra:member': [] as Agreement[],
      ...emptyApiResponse,
    },
    isFetching: agreementsFetching,
    isError: agreementsError,
  } = useGetFilteredAgreementsQuery({
    page,
    itemsPerPage,
    isActive: tabValue === AgreementsTabValue.active,
    ...(searchParams.get('type[]') && {
      type: searchParams.getAll('type[]'),
    }),
    ...(searchParams.get('variant[]') && {
      variant: searchParams.getAll('variant[]'),
    }),
  });

  const { t } = useTranslation();

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: AgreementsTabValue,
  ) => {
    setSearchParams((prevSearchParams) => {
      const newSearchParams = new URLSearchParams(prevSearchParams.toString());
      newSearchParams.delete('page');
      newSearchParams.set(
        'isActive',
        JSON.stringify(newValue === AgreementsTabValue.active),
      );

      return newSearchParams;
    });
    setTabValue(newValue);
  };

  return (
    <Grid item xs={10} bgcolor="background.list">
      <NavBarContainer>
        <Box display="flex" flexDirection="column">
          <NavBarTitle>{t('agreements.header')}</NavBarTitle>
        </Box>
        <Button
          variant="contained"
          onClick={() => setIsOpen(!isOpen)}
          endIcon={<AddCircleOutlineIcon />}
        >
          {t('button.create')}
        </Button>
      </NavBarContainer>
      <TabContext value={tabValue}>
        <StyledTabList
          onChange={handleTabChange}
          TabIndicatorProps={{ children: <span /> }}
        >
          <Tab label={t('label.active')} value={AgreementsTabValue.active} />
          <Tab
            label={t('label.inactive')}
            value={AgreementsTabValue.inactive}
          />
        </StyledTabList>
        <Filters
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
        <AgreementsList
          agreements={agreementsData['hydra:member']}
          agreementsError={agreementsError}
          agreementsFetching={agreementsFetching}
        />
        {agreementsData?.['hydra:view']?.['hydra:last'] && (
          <Box px={3} pb={3}>
            <CustomPagination
              apiData={agreementsData}
              itemsPerPage={itemsPerPage}
              page={page}
              handleOnPageChange={handleOnPageChange}
              handleOnItemsPerPageChange={handleOnItemsPerPageChange}
              showRowsSelect={false}
            />
          </Box>
        )}
      </TabContext>
      <Dialog open={isOpen} onClose={() => setIsOpen(!isOpen)} fullWidth>
        <AddAgreementForm isOpen={isOpen} setIsOpen={setIsOpen} />
      </Dialog>
    </Grid>
  );
}

export default Agreements;
